import React, { useRef, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import classnames from 'classnames';
import { FaPlus } from 'react-icons/fa';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import Server from "../../Shared/Server";
import { months_list } from '../../Shared/List';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const emptyCellFormatter = (params) =>
  params.value === null || params.value === undefined || params.value === '' ? '-' : params.value;

const ServerSideUnPaid = () => {
  const gridApi = useRef();
  const gridRef = useRef();
  const location = useLocation();

  const { payload } = location.state;
  const title = `${months_list[payload.month - 1].name} - ${payload.year} All Un-Paid Customers`;

  const columnDefs = useMemo(() => [
    {
      headerName: "#",
      filter: false,
      sortable: false,
      maxWidth: 70,
      valueFormatter: (params) => (params.node.rowIndex + 1).toString(),
    },
    {
      headerName: "Actions",
      minWidth: 80,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellRenderer: (e) => {
        const row = e.data;
        if (!row) return null;
        if (e.node.rowPinned) return '';
        const appointmentPayload = {
          location_id: row.location_pk,
          year: row.year,
          month: row.month,
          contact: row.contact_number,
        };
        return (
          <div className="d-flex gap-1">
            <Link
              className={classnames('btn btn-outline-info', { disabled: row.has_an_appointment === true })}
              to="/add-payment-appointment"
              state={{ payload: appointmentPayload }}
              title={row.has_an_appointment ? "Already has appointment" : "Add Appointment"}
            >
              <FaPlus />
            </Link>
          </div>
        );
      },
    },
    { headerName: "PID", field: "previous_id", minWidth: 110 },
    { headerName: "ID", field: "location_id", minWidth: 110 },
    { headerName: "Name", field: "full_name", minWidth: 220 },
    { headerName: "Remark", field: "remark" },
    { headerName: "Contact", field: "contact_number" },
    { headerName: "Prev.Reading", field: "previous_reading" },
    { headerName: "Curr.Reading", field: "current_reading" },
    { headerName: "Difference", field: "difference" },
    { headerName: "Prev.Balance", field: "previous_balance" },
    { headerName: "Debit", field: "debit" },
    { headerName: "Total", field: "total" },
    {
      headerName: "Period",
      field: "period",
      valueFormatter: (params) => params.value ? params.value : "1 Bille"
    },
    { headerName: "Meter ID", field: "serial_number" },
    { headerName: "Lock ID", field: "lock_number" },
    { headerName: "Rate", field: "rate_name" },
    { headerName: "Village", field: "village_name" },
    { headerName: "Zone", field: "zone_name" },
    { headerName: "Station", field: "station_name" },
    { headerName: "Line", field: "line_name" },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: false,
    filter: true,
    resizable: true,
    valueFormatter: emptyCellFormatter,
    filterParams: {
      buttons: []
    }
  }), []);

  const fetchData = async (params) => {
    const dataColumns = columnDefs.filter(col => col.field);
    const requestParams = {
      draw: 1,
      start: params.startRow,
      length: params.endRow - params.startRow,
      year: payload.year,
      month: payload.month,
      columns: dataColumns.map(col => ({
        data: col.field,
        searchable: col.filter !== false,
        orderable: !!col.sortable,
        search: { value: '', regex: false }
      })),
      order: params.sortModel.map(sort => ({
        column: dataColumns.findIndex(col => col.field === sort.colId),
        dir: sort.sort
      })),
      search: Object.keys(params.filterModel).map(key => ({
        col: key,
        value: params.filterModel[key].filter?.toString().toLowerCase() ?? '',
        type: params.filterModel[key].type || "contains"
      })),
      filter: {}
    };

    try {
      const response = await Server.post('/un-paid/serverside', requestParams);
      params.successCallback(response.data.data, response.data.recordsFiltered);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load un-paid');
      params.failCallback();
    }
  };

  const onGridReady = (params) => {
    gridApi.current = params.api;
    params.api.updateGridOptions({
      datasource: {
        getRows: (p) => fetchData(p)
      }
    });
  };

  const onFirstDataRendered = (params) => {
    params.api.autoSizeAllColumns();
  };

  const onGridSizeChanged = (params) => {
    params.api.autoSizeAllColumns();
  };

  return (
    <>
      <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
          <h3 className="card-title mb-0">{title}</h3>
        </div>
        <div className="card-body">
          <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowModelType="infinite"
              cacheBlockSize={100}
              paginationPageSize={100}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              enableCellTextSelection={true}
              suppressCellFocus={true}
              onGridReady={onGridReady}
              onFirstDataRendered={onFirstDataRendered}
              onGridSizeChanged={onGridSizeChanged}
            />
          </div>
        </div>
      </div>
      <br />
    </>
  );
};

export default ServerSideUnPaid;
